import React, { useContext, useEffect, useState } from "react";
import axios from "axios";

import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  FormControlLabel,
  Grow,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  Paper,
  Select,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";

import {
  AddCircleRounded,
  AdminPanelSettings as AdminIcon,
  ArrowDownward,
  ArrowUpward,
  BadgeRounded,
  Block as BlockIcon,
  Business as InstitutionIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  GroupsRounded,
  Password as PasswordIcon,
  Person as PersonIcon,
  PersonOutlineRounded,
  SaveRounded,
  SchoolRounded,
  Search as SearchIcon,
  SettingsRounded,
  UnfoldMore,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
  Work as AnalystIcon,
  AccountCircleRounded,
  ManageAccountsRounded,
} from "@mui/icons-material";

import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

import { api } from "@api/api";
import RegisterAnalystSRE from "../secretaria/pageServInt";
import { botaoAdicionar } from "../../estilos/botoes";
import { CondicaoContext } from "../../contexto";
import PaginacaoLista from "../../componentes/comuns/PaginacaoLista";
import TransicaoPagina from "../../componentes/comuns/TransicaoPagina";

import {
  coresTextosGlobais, iconesGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../estilos/global";

// ====================================================
// PADRÃO DE LISTAS DO SEAR
// ====================================================

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const ESTILO_ACAO_EDITAR = {
  width: 30,
  height: 30,
  p: 0,
  color: "var(--sear-azul, #087cf0)",
  backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
  border: "1px solid var(--sear-borda, #d7eaff)",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "var(--sear-azul, #0767d8)",
    backgroundColor: "var(--sear-superficie-azul, #dceeff)",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_EXCLUIR = {
  width: 30,
  height: 30,
  p: 0,
  color: "var(--sear-erro-texto, #e53935)",
  backgroundColor: "var(--sear-erro-fundo, #ffeded)",
  border: "1px solid var(--sear-erro-borda, #ffdcdc)",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "var(--sear-erro-texto, #c62828)",
    backgroundColor: "var(--sear-erro-fundo, #ffdddd)",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_BLOQUEADA = {
  width: 30,
  height: 30,
  p: 0,
  border: "1px solid var(--sear-borda, #e0e8ef)",
  borderRadius: "50%",
  "&.Mui-disabled": {
    color: "var(--sear-texto-suave, #9fb7ce)",
    backgroundColor: "var(--sear-superficie-azul, #f3f7fa)",
  },
};

// ====================================================
// ESTILOS LEGADOS DO ADMIN — PRESERVADOS
// ====================================================

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(31, 38, 135, 0.15)",
  background: "linear-gradient(to bottom right, var(--sear-superficie, #ffffff), var(--sear-superficie-suave, #f8f9fa))",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 32px rgba(31, 38, 135, 0.25)",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    animation: `${pulse} 0.5s ease`,
  },
  "&:last-child td": {
    borderBottom: 0,
  },
}));

// ====================================================
// COMPONENTE
// ====================================================

const UsuariosList = () => {
  const { role } = useContext(CondicaoContext);
  const tituloSre = role === "superintendent";

  const [cadastroAnalistaAberto, setCadastroAnalistaAberto] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroRegra, setFiltroRegra] = useState("todos");
  const [termoPesquisa, setTermoPesquisa] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);

  const [ordem, setOrdem] = useState({
    coluna: "nome",
    direcao: "asc",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

  const [confirmacaoMarcada, setConfirmacaoMarcada] = useState(false);

  // Acesso à Gestão permanece funcional.
  // Sua coluna fica oculta exclusivamente na SRE.
  const [acessosUsuario, setAcessosUsuario] = useState({});

  const [nomeEditado, setNomeEditado] = useState("");
  const [emailEditado, setEmailEditado] = useState("");

  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");

  const [requisitosSenha, setRequisitosSenha] = useState({
    minimoCaracteres: false,
    letraMaiuscula: false,
    numero: false,
    caractereEspecial: false,
    senhasConferem: false,
  });

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);
  const criteriosSenhaAtendidos = [
  requisitosSenha.minimoCaracteres,
  requisitosSenha.letraMaiuscula,
  requisitosSenha.numero,
  requisitosSenha.caractereEspecial,
].filter(Boolean).length;

  // ====================================================
  // CONSULTA DOS USUÁRIOS
  // ====================================================

  useEffect(() => {
    buscarUsuarios();
  }, []);

  const buscarUsuarios = async () => {
    setLoading(true);

    try {
      const response = await axios.get(api.adminListUsers(), {
        withCredentials: true,
      });

      setUsuarios(response.data);

      const acessosIniciais = {};

      response.data.forEach((user) => {
        if (user.regra === "analyst") {
          acessosIniciais[user.id] = user.condicao === true;
        }
      });

      setAcessosUsuario(acessosIniciais);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // VALIDAÇÃO DA SENHA
  // ====================================================

  const validarSenha = (senha, confirmacao) => {
    setRequisitosSenha({
      minimoCaracteres: senha.length >= 8,
      letraMaiuscula: /[A-Z]/.test(senha),
      numero: /[0-9]/.test(senha),
      caractereEspecial: /[!@#$%^&*(),.?":{}|<>]/.test(senha),
      senhasConferem: senha === confirmacao && senha !== "",
    });
  };

  // ====================================================
  // ACESSO À GESTÃO — PRESERVADO
  // ====================================================

  const handleAcessoUsuariosChange = async (userId, checked) => {
    try {
      setAcessosUsuario((prev) => ({
        ...prev,
        [userId]: checked,
      }));

      await axios.patch(
        api.adminUpdateUserAccess(userId),
        {
          temAcessoUsuarios: checked,
        },
        {
          withCredentials: true,
        },
      );

      buscarUsuarios();
    } catch (error) {
      console.error("Erro ao atualizar acesso:", error);

      setAcessosUsuario((prev) => ({
        ...prev,
        [userId]: !checked,
      }));
    }
  };

  // ====================================================
  // EXCLUSÃO
  // ====================================================

  const abrirDialogExclusao = (usuario) => {
    setUsuarioSelecionado(usuario);
    setDialogOpen(true);
    setConfirmacaoMarcada(false);
  };

  const confirmarExclusao = async () => {
    if (!usuarioSelecionado?.id) {
      return;
    }

    try {
      await axios.delete(api.adminDeleteUsers(usuarioSelecionado.id), {
        withCredentials: true,
      });

      setDialogOpen(false);
      setUsuarioSelecionado(null);
      setConfirmacaoMarcada(false);

      buscarUsuarios();
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);

      if (error.response) {
        console.error("Resposta do servidor:", error.response.data);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setConfirmacaoMarcada(false);
  };

  // ====================================================
  // EDIÇÃO
  // ====================================================

  const abrirDialogEdicao = (usuario) => {
    setUsuarioSelecionado(usuario);
    setNomeEditado(usuario.nome || "");
    setEmailEditado(usuario.email);

    setNovaSenha("");
    setConfirmarSenha("");
    setEditDialogOpen(true);

    setRequisitosSenha({
      minimoCaracteres: false,
      letraMaiuscula: false,
      numero: false,
      caractereEspecial: false,
      senhasConferem: false,
    });
  };

  const confirmarEdicao = async () => {
    if (!usuarioSelecionado?.id) {
      return;
    }

    if (novaSenha && novaSenha !== confirmarSenha) {
      alert("As senhas não coincidem!");
      return;
    }

    if (
      novaSenha &&
      !(
        requisitosSenha.minimoCaracteres &&
        requisitosSenha.letraMaiuscula &&
        requisitosSenha.numero &&
        requisitosSenha.caractereEspecial
      )
    ) {
      alert("A senha não atende a todos os requisitos de segurança!");
      return;
    }

    try {
      const dadosAtualizacao = {
        email: emailEditado,
      };

      if (usuarioSelecionado.regra === "analyst") {
        dadosAtualizacao.nome = nomeEditado.trim();
      }

      if (novaSenha) {
        dadosAtualizacao.senha = novaSenha;
      }

      await axios.patch(
        api.adminUpdateUser(usuarioSelecionado.id),
        dadosAtualizacao,
        {
          withCredentials: true,
        },
      );

      setEditDialogOpen(false);
      setUsuarioSelecionado(null);

      buscarUsuarios();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);

      if (error.response) {
        console.error("Resposta do servidor:", error.response.data);
      }
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setUsuarioSelecionado(null);
  };

  const handleSenhaChange = (e) => {
    setNovaSenha(e.target.value);
    validarSenha(e.target.value, confirmarSenha);
  };

  const handleConfirmarSenhaChange = (e) => {
    setConfirmarSenha(e.target.value);
    validarSenha(novaSenha, e.target.value);
  };

  // ====================================================
  // PESQUISA E FILTROS
  // ====================================================

  const usuariosFiltrados = usuarios.filter((usuario) => {
    if (filtroRegra !== "todos" && usuario.regra !== filtroRegra) {
      return false;
    }

    const termo = termoPesquisa.trim().toLocaleLowerCase("pt-BR");

    if (!termo) {
      return true;
    }

    return (
      String(usuario.nome || "")
        .toLocaleLowerCase("pt-BR")
        .includes(termo) ||
      String(usuario.email || "")
        .toLocaleLowerCase("pt-BR")
        .includes(termo)
    );
  });

  const getDescricaoRegra = (regra) => {
    switch (regra) {
      case "admin":
        return "Administrador";
      case "analyst":
        return "Analista";
      case "user":
        return tituloSre ? "Escola" : "Instituição";
      default:
        return tituloSre ? "Não identificado" : "";
    }
  };

  const getIconByRole = (regra) => {
    if (tituloSre) {
      switch (regra) {
        case "admin":
          return <AdminIcon color="error" />;
        case "analyst":
          return <BadgeRounded sx={{ color: "var(--sear-azul, #087cf0)" }} />;
        case "user":
          return <SchoolRounded sx={{ color: "var(--sear-azul, #087cf0)" }} />;
        default:
          return <PersonIcon color="disabled" />;
      }
    }

    switch (regra) {
      case "admin":
        return <AdminIcon color="error" />;
      case "analyst":
        return <AnalystIcon color="primary" />;
      case "user":
        return <InstitutionIcon color="success" />;
      default:
        return <PersonIcon color="disabled" />;
    }
  };

  // ====================================================
  // ORDENAÇÃO
  // ====================================================

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPaginaAtual(1);
  };

  const usuariosOrdenados = tituloSre
    ? [...usuariosFiltrados].sort((a, b) => {
        const valorA =
          ordem.coluna === "regra"
            ? getDescricaoRegra(a.regra)
            : String(a[ordem.coluna] || "");

        const valorB =
          ordem.coluna === "regra"
            ? getDescricaoRegra(b.regra)
            : String(b[ordem.coluna] || "");

        const resultado = valorA.localeCompare(valorB, "pt-BR", {
          sensitivity: "base",
          numeric: true,
        });

        return ordem.direcao === "asc" ? resultado : -resultado;
      })
    : usuariosFiltrados;

  // ====================================================
  // PAGINAÇÃO
  // ====================================================

  const totalRegistros = usuariosOrdenados.length;

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / itensPorPagina));

  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  const inicio = (paginaSegura - 1) * itensPorPagina;

  const fim = Math.min(inicio + itensPorPagina, totalRegistros);

  const usuariosExibidos = tituloSre
    ? usuariosOrdenados.slice(inicio, fim)
    : usuariosOrdenados;

  const resumoResultados = totalRegistros
    ? `Mostrando ${inicio + 1} a ${fim} de ${totalRegistros} resultados`
    : "Mostrando 0 resultados";

  // ====================================================
  // CABEÇALHO ORDENÁVEL
  // ====================================================

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          color: coresTextosGlobais.cabecalhoTabela,
          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
          fontWeight: 700,
          "&:hover": {
            color: "var(--sear-azul, #0767d8)",
          },
          "&.Mui-active": {
            color: coresTextosGlobais.cabecalhoTabela,
          },
          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: "var(--sear-azul, #385b90)",
            fontSize: iconesGlobais.conteudoTabela.fontSize,
            ml: 0.35,
            transform: "none !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
          }}
        >
          <Icone
            sx={{
              color: "var(--sear-azul, #087cf0)",
              fontSize: iconesGlobais.tabela.fontSize,
              flexShrink: 0,
            }}
          />
          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  // ====================================================
  // PAGINAÇÃO COMPARTILHADA
  // ====================================================

  const renderPaginacao = (inferior = false) => {
    if (!tituloSre || totalRegistros === 0) {
      return null;
    }

    return (
      <Box
        sx={
          inferior
            ? {
                display: "flex",
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                },
                justifyContent: "space-between",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 0.75,
                mt: 0.45,
              }
            : {
                display: "flex",
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                },
                justifyContent: "space-between",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 0.75,
                px: 1.5,
                py: 0.35,
                mb: 0.5,
                border: "1px solid var(--sear-borda, #d7e8f8)",
                borderRadius: 1.5,
                backgroundColor: "var(--sear-superficie-azul, #f2f8fe)",
              }
        }
      >
        <Typography
          variant="caption"
          sx={{
            color: inferior ? "var(--sear-texto-secundario, #526b8d)" : "var(--sear-texto-secundario, #536b8b)",
            fontSize: tamanhosControlesGlobais.contador,
          }}
        >
          {inferior
            ? resumoResultados
            : `${totalRegistros} ${
                totalRegistros === 1 ? "registro" : "registros"
              }`}
        </Typography>

        <Box sx={{ ml: { sm: "auto" } }}>
          <PaginacaoLista
            paginaAtual={paginaSegura}
            totalPaginas={totalPaginas}
            onChange={setPaginaAtual}
            ariaLabel={
              inferior
                ? "Paginação inferior da lista de usuários"
                : "Paginação superior da lista de usuários"
            }
          />
        </Box>
      </Box>
    );
  };

  // ====================================================
  // CARREGAMENTO
  // ====================================================

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Fade in={true} style={{ transitionDelay: "200ms" }}>
          <CircularProgress size={60} thickness={4} />
        </Fade>
      </Box>
    );
  }

  // ====================================================
  // PÁGINA
  // ====================================================

  const pagina = (
    <Box
      sx={
        tituloSre
          ? {
              p: 0,
              display: "flex",
              flexDirection: "column",
              flex: "1 1 0",
              minHeight: 0,
              overflow: "hidden",
              "& > *": {
                flexShrink: 0,
              },
            }
          : { p: 3 }
      }
    >
      {/* CABEÇALHO */}

      <Grow in={true} appear={role !== "superintendent"}>
        {tituloSre ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.4,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  borderRadius: 1.5,
                  color: "var(--sear-azul, #087cf0)",
                  backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
                }}
              >
                <ManageAccountsRounded sx={{ fontSize: 27 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: coresTextosGlobais.tituloPagina,
                    fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                    fontWeight: 700,
                    lineHeight: 1.15,
                  }}
                >
                  Gerenciamento de Usuários
                </Typography>

                <Typography
                  sx={{
                    mt: 0,
                    color: "var(--sear-texto-secundario, #7b8798)",
                    fontSize: tamanhosControlesGlobais.descricao,
                    lineHeight: 1.35,
                  }}
                >
                  Consulte e gerencie os usuários do sistema.
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              size="small"
              startIcon={
                <AddCircleRounded sx={{ fontSize: "18px !important" }} />
              }
              onClick={() => setCadastroAnalistaAberto(true)}
              sx={{
                ...botaoAdicionar,
                fontSize: tamanhosControlesGlobais.botao,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Cadastrar Analista
            </Button>
          </Box>
        ) : (
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: "bold",
              background: "linear-gradient(45deg, var(--sear-azul, #1976d2), var(--sear-azul, #2196f3))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Gerenciamento de Usuários
          </Typography>
        )}
      </Grow>

      {/* ====================================================
          BUSCA E FILTROS
      ==================================================== */}

      <Slide
        direction="down"
        in={true}
        appear={role !== "superintendent"}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={
            tituloSre
              ? {
                  display: "flex",
                  alignItems: {
                    xs: "stretch",
                    md: "center",
                  },
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  gap: 1.25,
                  px: 1.25,
                  py: 0.55,
                  mb: 0.5,
                  border: "1px solid var(--sear-borda, #d5e7f7)",
                  borderRadius: 1.5,
                  backgroundColor: "var(--sear-superficie-azul, #f7fbff)",
                }
              : {
                  mb: 4,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }
          }
        >
          {tituloSre ? (
            <>
              {/* PESQUISA E TIPO DE USUÁRIO */}

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: {
                    xs: "stretch",
                    sm: "center",
                  },
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  flexWrap: "wrap",
                  gap: 1.25,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Pesquisar usuário ou e-mail..."
                  inputProps={{
                    "aria-label": "Pesquisar usuário ou e-mail",
                  }}
                  value={termoPesquisa}
                  onChange={(event) => {
                    setTermoPesquisa(event.target.value);
                    setPaginaAtual(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            color: "var(--sear-azul, #087cf0)",
                            fontSize: 18,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: 440,
                    },
                    maxWidth: "100%",
                    "& .MuiOutlinedInput-root": {
                      height: 34,
                      backgroundColor: "var(--sear-superficie, #ffffff)",
                      borderRadius: 1,
                      "& fieldset": {
                        borderColor: "var(--sear-borda, #d7e0ea)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--sear-borda, #a9c9e8)",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: tamanhosControlesGlobais.campoBusca,
                    },
                  }}
                />

                <Select
                  size="small"
                  value={filtroRegra}
                  onChange={(event) => {
                    setFiltroRegra(event.target.value);
                    setPaginaAtual(1);
                  }}
                  inputProps={{
                    "aria-label": "Filtrar tipo de usuário",
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsRounded
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: 18,
                        }}
                      />
                    </InputAdornment>
                  }
                  sx={{
                    width: {
                      xs: "100%",
                      sm: 210,
                    },
                    height: 34,
                    backgroundColor: "var(--sear-superficie, #ffffff)",
                    color: "var(--sear-azul, #314d76)",
                    borderRadius: 1,
                    fontSize: tamanhosControlesGlobais.seletor,
                    fontWeight: 500,
                    "& .MuiSelect-select": {
                      py: 0.55,
                    },
                  }}
                >
                  <MenuItem value="todos">Todos os tipos</MenuItem>

                  <MenuItem value="analyst">Analistas</MenuItem>

                  <MenuItem value="user">Escolas</MenuItem>
                </Select>
              </Box>

              {/* REGISTROS POR PÁGINA */}

              <Box
                sx={{
                  display: {
                    xs: "none",
                    md: "block",
                  },
                  width: "1px",
                  height: 30,
                  flexShrink: 0,
                  backgroundColor: "var(--sear-superficie-azul, #d8e7f4)",
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: {
                    xs: "space-between",
                    md: "flex-end",
                  },
                  gap: 1.2,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--sear-texto-secundario, #667b98)",
                    fontSize: tamanhosControlesGlobais.rotulo,
                    whiteSpace: "nowrap",
                  }}
                >
                  Registros por página:
                </Typography>

                <Select
                  size="small"
                  value={itensPorPagina}
                  onChange={(event) => {
                    setItensPorPagina(Number(event.target.value));
                    setPaginaAtual(1);
                  }}
                  renderValue={(valor) => `${valor} por página`}
                  inputProps={{
                    "aria-label": "Registros por página",
                  }}
                  sx={{
                    width: 160,
                    height: 34,
                    backgroundColor: "var(--sear-superficie, #ffffff)",
                    color: "var(--sear-azul, #314d76)",
                    borderRadius: 1,
                    fontSize: tamanhosControlesGlobais.seletor,
                    fontWeight: 500,
                    "& .MuiSelect-select": {
                      py: 0.55,
                    },
                  }}
                >
                  {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
                    <MenuItem key={quantidade} value={quantidade}>
                      {quantidade} por página
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </>
          ) : (
            <>
              {/* PESQUISA ORIGINAL DOS DEMAIS PERFIS */}

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Pesquisar usuários..."
                value={termoPesquisa}
                onChange={(event) => setTermoPesquisa(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{
                        color: "action.active",
                        mr: 1,
                      }}
                    />
                  ),
                  sx: {
                    borderRadius: "50px",
                    backgroundColor: "background.paper",
                    boxShadow: 1,
                    fontSize:
                      role === "admin"
                        ? tamanhosControlesGlobais.campoBusca
                        : undefined,
                  },
                }}
                sx={{
                  flex: 1,
                  minWidth: 250,
                }}
              />

              {role === "admin" && (
                <Box sx={{ minWidth: 200 }}>
                  <Select
                    fullWidth
                    displayEmpty
                    value={filtroRegra}
                    onChange={(event) => setFiltroRegra(event.target.value)}
                    renderValue={(valor) => (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <FilterIcon sx={{ fontSize: 20 }} />
                        {valor === "todos"
                          ? "Todos os Usuários"
                          : valor === "analyst"
                            ? "Analistas"
                            : valor === "admin"
                              ? "Administradores"
                              : "Instituições"}
                      </Box>
                    )}
                    sx={{
                      borderRadius: "50px",
                      backgroundColor: "background.paper",
                      boxShadow: 1,
                      fontSize: tamanhosControlesGlobais.seletor,
                    }}
                  >
                    <MenuItem value="todos">Todos os Usuários</MenuItem>
                    <MenuItem value="analyst">Analistas</MenuItem>
                    <MenuItem value="admin">Administradores</MenuItem>
                    <MenuItem value="user">Instituições</MenuItem>
                  </Select>
                </Box>
              )}
            </>
          )}
        </Box>
      </Slide>

      {/* PAGINAÇÃO SUPERIOR */}

      {renderPaginacao()}

      {/* ====================================================
          TABELA DE USUÁRIOS
      ==================================================== */}

      <Zoom
        in={true}
        appear={role !== "superintendent"}
        style={{ transitionDelay: "300ms" }}
      >
        {tituloSre ? (
          <TableContainer
            data-rolagem-conteudo=""
            component={Paper}
            sx={{
              borderRadius: 1.5,
              border: "1px solid var(--sear-borda, #d7e7f7)",
              boxShadow: "none",
              overflowX: "auto",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: 720,
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  borderColor: "var(--sear-borda, #dfe9f3)",
                },
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: "var(--sear-superficie-azul, #edf6fe)",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      width: "27%",
                      px: 1.4,
                      py: 0.55,
                      borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "Usuário",
                      "nome",
                      AccountCircleRounded,
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "33%",
                      px: 1.4,
                      py: 0.55,
                      borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                    }}
                  >
                    {renderCabecalhoOrdenavel("E-mail", "email", EmailIcon)}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "25%",
                      px: 1.4,
                      py: 0.55,
                      borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "Tipo de usuário",
                      "regra",
                      GroupsRounded,
                    )}
                  </TableCell>

                  {/* ACESSO À GESTÃO OCULTO NA SRE */}

                  <TableCell
                    align="center"
                    sx={{
                      width: "15%",
                      px: 1,
                      py: 0.55,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.65,
                        color: "var(--sear-azul, #27456f)",
                      }}
                    >
                      <SettingsRounded
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: iconesGlobais.tabela.fontSize,
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: coresTextosGlobais.cabecalhoTabela,
                          fontSize:
                            tamanhosPaginasAdministrativas.cabecalhoTabela,
                          fontWeight: 700,
                        }}
                      >
                        Ações
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {usuariosExibidos.length ? (
                  usuariosExibidos.map((usuario) => (
                    <TableRow
                      key={usuario.id}
                      hover
                      sx={{
                        height: 38,
                        "&:hover": {
                          backgroundColor: "var(--sear-superficie, #fbfdff)",
                        },
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          px: 1.4,
                          py: 0.35,
                          borderRight: "1px solid var(--sear-borda, #e5edf5)",
                        }}
                      >
                        <Tooltip title={usuario.nome || ""}>
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => abrirDialogEdicao(usuario)}
                            aria-label={`Editar ${usuario.nome || "usuário"}`}
                            sx={{
                              display: "block",
                              maxWidth: "100%",
                              border: 0,
                              padding: 0,
                              background: "transparent",
                              fontFamily: "inherit",
                              textAlign: "left",
                              cursor: "pointer",
                              "&:hover, &:focus-visible": {
                                textDecoration: "underline",
                              },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: coresTextosGlobais.link,
                              fontSize:
                                tamanhosPaginasAdministrativas.conteudoTabela,
                              fontWeight: 600,
                            }}
                          >
                            {usuario.nome}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          px: 1.4,
                          py: 0.35,
                          borderRight: "1px solid var(--sear-borda, #e5edf5)",
                        }}
                      >
                        <Tooltip title={usuario.email || ""}>
                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: coresTextosGlobais.conteudoTabela,
                              fontSize:
                                tamanhosPaginasAdministrativas.conteudoTabela,
                            }}
                          >
                            {usuario.email || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          px: 1.4,
                          py: 0.35,
                          borderRight: "1px solid var(--sear-borda, #e5edf5)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              flexShrink: 0,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 1,
                              backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
                              "& svg": {
                                fontSize: iconesGlobais.tabela.fontSize,
                              },
                            }}
                          >
                            {getIconByRole(usuario.regra)}
                          </Box>

                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: coresTextosGlobais.conteudoTabela,
                              fontSize:
                                tamanhosPaginasAdministrativas.conteudoTabela,
                            }}
                          >
                            {getDescricaoRegra(usuario.regra)}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          px: 0.5,
                          py: 0.35,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.85,
                          }}
                        >
                          <Tooltip title="Editar usuário">
                            <IconButton
                              size="small"
                              aria-label={`Editar ${usuario.nome}`}
                              onClick={() => abrirDialogEdicao(usuario)}
                              sx={ESTILO_ACAO_EDITAR}
                            >
                              <EditIcon
                                sx={{
                                  fontSize: iconesGlobais.tabela.fontSize,
                                }}
                              />
                            </IconButton>
                          </Tooltip>

                          {usuario.regra !== "user" ? (
                            <Tooltip title="Excluir usuário">
                              <IconButton
                                size="small"
                                aria-label={`Excluir ${usuario.nome}`}
                                onClick={() => abrirDialogExclusao(usuario)}
                                sx={ESTILO_ACAO_EXCLUIR}
                              >
                                <DeleteIcon
                                  sx={{
                                    fontSize: iconesGlobais.tabela.fontSize,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Instituições não podem ser excluídas">
                              <span>
                                <IconButton
                                  disabled
                                  size="small"
                                  aria-label="Exclusão indisponível"
                                  sx={ESTILO_ACAO_BLOQUEADA}
                                >
                                  <BlockIcon
                                    sx={{
                                      fontSize: iconesGlobais.tabela.fontSize,
                                    }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        py: 5,
                        color: "text.secondary",
                        fontSize: tamanhosControlesGlobais.descricao,
                      }}
                    >
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* TABELA ORIGINAL DOS DEMAIS PERFIS */

          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "var(--sear-acao-azul, #1976d2)",
                  }}
                >
                  {[
                    "Usuário",
                    "Email",
                    "Regra",
                    "Acesso à Gestão",
                    "Ações",
                  ].map((titulo) => (
                    <StyledTableCell
                      key={titulo}
                      sx={{
                        color: "primary.contrastText",
                      }}
                    >
                      {titulo}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {usuariosExibidos.length > 0 ? (
                  usuariosExibidos.map((usuario) => (
                    <StyledTableRow key={usuario.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            sx={{
                              mr: 2,
                              bgcolor: "var(--sear-acao-azul, #1976d2)",
                            }}
                          >
                            {(usuario.nome || "").charAt(0).toUpperCase()}
                          </Avatar>
                          {usuario.nome}
                        </Box>
                      </TableCell>

                      <TableCell>{usuario.email}</TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getIconByRole(usuario.regra)}

                          <Typography
                            variant="body2"
                            sx={{
                              ml: 1,
                              fontSize:
                                role === "admin"
                                  ? tamanhosPaginasAdministrativas.conteudoTabela
                                  : undefined,
                            }}
                          >
                            {getDescricaoRegra(usuario.regra)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* FUNCIONALIDADE DE ACESSO PRESERVADA */}

                      <TableCell>
                        {usuario.regra === "analyst" ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={acessosUsuario[usuario.id] || false}
                                onChange={(e) =>
                                  handleAcessoUsuariosChange(
                                    usuario.id,
                                    e.target.checked,
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="Acesso à gestão"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize:
                                  role === "admin"
                                    ? tamanhosPaginasAdministrativas.conteudoTabela
                                    : undefined,
                              },
                            }}
                          />
                        ) : (
                          <Tooltip title="Disponível apenas para analistas">
                            <Box
                              sx={{
                                color: "text.disabled",
                                fontStyle: "italic",
                              }}
                            >
                              Não aplicável
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Editar usuário">
                            <IconButton
                              color="primary"
                              onClick={() => abrirDialogEdicao(usuario)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          {usuario.regra !== "user" ? (
                            <Tooltip title="Excluir usuário">
                              <IconButton
                                color="error"
                                onClick={() => abrirDialogExclusao(usuario)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Instituições não podem ser excluídas">
                              <span>
                                <IconButton
                                  disabled
                                  sx={{
                                    color: "text.disabled",
                                  }}
                                >
                                  <BlockIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        sx={{
                          fontSize:
                            role === "admin"
                              ? tamanhosControlesGlobais.descricao
                              : undefined,
                        }}
                      >
                        Nenhum usuário encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </Zoom>

      {/* PAGINAÇÃO INFERIOR */}

      {renderPaginacao(true)}

      {/* ====================================================
          CADASTRO DE ANALISTA
      ==================================================== */}

      {tituloSre && cadastroAnalistaAberto && (
        <RegisterAnalystSRE
          emModal
          onCancelar={() => setCadastroAnalistaAberto(false)}
          onCadastrado={() => {
            setCadastroAnalistaAberto(false);
            buscarUsuarios();
          }}
        />
      )}

      {/* ====================================================
          CONFIRMAÇÃO DE EXCLUSÃO — PRESERVADA
      ==================================================== */}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        TransitionComponent={Slide}
        transitionDuration={300}
      >
        <DialogTitle
          sx={{
            bgcolor: "error.main",
            color: "error.contrastText",
            display: "flex",
            alignItems: "center",
          }}
        >
          <WarningIcon
            sx={{
              mr: 10,
              mt: 1,
            }}
          />
          Confirmar Exclusão
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mt={6} mb={2}>
            <Avatar
              sx={{
                bgcolor: "error.main",
                mr: 2,
              }}
            >
              <DeleteIcon />
            </Avatar>

            <Typography variant="body1">
              Você está prestes a excluir permanentemente o usuário:
            </Typography>
          </Box>

          <Box pl={7} mb={2}>
            <Typography variant="h6" gutterBottom>
              <strong>{usuarioSelecionado?.nome}</strong>
            </Typography>

            <Typography variant="body2" color="textSecondary">
              {usuarioSelecionado?.email}
            </Typography>

            <Typography variant="caption" color="error" display="block" mt={1}>
              Esta ação não pode ser desfeita!
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmacaoMarcada}
                onChange={(e) => setConfirmacaoMarcada(e.target.checked)}
                color="error"
              />
            }
            label="Eu entendo que esta ação é irreversível e desejo prosseguir"
            sx={{ ml: 0 }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: "50px",
            }}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            onClick={confirmarExclusao}
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: "50px",
            }}
            disabled={!confirmacaoMarcada}
          >
            Confirmar Exclusão
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====================================================
          EDIÇÃO DE USUÁRIO — PRESERVADA
      ==================================================== */}

      <Dialog
        open={editDialogOpen && !tituloSre}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={
          tituloSre
            ? {
                sx: {
                  borderTop: "3px solid var(--sear-borda-forte, #087cf0)",
                  borderRadius: "10px",
                  overflow: "hidden",
                },
              }
            : undefined
        }
      >
        <DialogTitle
          sx={
            tituloSre
              ? {
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 2,
                  bgcolor: "var(--sear-superficie, #ffffff)",
                  borderBottom: "1px solid var(--sear-borda, #e5edf5)",
                }
              : {
                  bgcolor: "var(--sear-acao-azul, #1976d2)",
                  color: "primary.contrastText",
                  display: "flex",
                  alignItems: "center",
                }
          }
        >
          {tituloSre ? (
            <>
              <Box
                aria-hidden="true"
                sx={{
                  width: 42,
                  height: 42,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 30,
                    height: 30,
                    color: "var(--sear-azul, #087cf0)",
                  }}
                >
                  <PersonOutlineRounded sx={{ fontSize: 28 }} />
                  <EditIcon
                    sx={{
                      position: "absolute",
                      right: -3,
                      bottom: -1,
                      fontSize: 15,
                      bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
                      borderRadius: 0.5,
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    color: "var(--sear-azul, #0767d8)",
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {usuarioSelecionado?.regra === "analyst"
                    ? "Editar analista"
                    : "Editar usuário"}
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mt: 0.3,
                    color: "var(--sear-texto-secundario, #7b8798)",
                    fontSize: tamanhosControlesGlobais.descricao,
                  }}
                >
                  Atualize os dados do usuário cadastrado.
                </Typography>
              </Box>

              <IconButton
                aria-label="Fechar edição"
                onClick={handleCloseEditDialog}
                size="small"
                sx={{
                  color: "var(--sear-texto-secundario, #71839a)",
                  alignSelf: "flex-start",
                }}
              >
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <>
              <EditIcon sx={{ mr: 1 }} />
              Editar Usuário
            </>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box mb={3}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                sx={{
                  bgcolor: "var(--sear-acao-azul, #1976d2)",
                  mr: 2,
                }}
              >
                {usuarioSelecionado?.nome?.charAt(0).toUpperCase()}
              </Avatar>

              {usuarioSelecionado?.regra === "analyst" ? (
                <TextField
                  fullWidth
                  label="Nome do Analista"
                  variant="standard"
                  value={nomeEditado}
                  onChange={(e) => setNomeEditado(e.target.value)}
                  sx={{ flex: 1 }}
                />
              ) : (
                <Typography variant="h6">{usuarioSelecionado?.nome}</Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={emailEditado}
              onChange={(e) => setEmailEditado(e.target.value)}
              InputProps={{
                startAdornment: (
                  <EmailIcon
                    sx={{
                      color: "action.active",
                      mr: 1,
                    }}
                  />
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Alterar Senha
            </Typography>

            <DialogContentText sx={{ mb: 2 }}>
              Deixe em branco para manter a senha atual
            </DialogContentText>

<TextField
  fullWidth
  label="Nova Senha"
    type={mostrarSenha ? "text" : "password"}
  autoComplete="new-password"
              variant="outlined"
              value={novaSenha}
              onChange={handleSenhaChange}
              InputProps={{
                startAdornment: (
                  <PasswordIcon
                    sx={{
                      color: "action.active",
                      mr: 1,
                    }}
                  />
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      edge="end"
                    >
                      {mostrarSenha ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
  label="Confirmar Senha"
  type={mostrarConfirmarSenha ? "text" : "password"}
  autoComplete="new-password"
              variant="outlined"
              value={confirmarSenha}
              onChange={handleConfirmarSenhaChange}
              InputProps={{
                startAdornment: (
                  <PasswordIcon
                    sx={{
                      color: "action.active",
                      mr: 1,
                    }}
                  />
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                      }
                      edge="end"
                    >
                      {mostrarConfirmarSenha ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {novaSenha && (
              <Box mt={2}>
                <Typography variant="subtitle2">
                  Requisitos da senha:
                </Typography>

                <List dense>
                  {[
                    ["minimoCaracteres", "Mínimo de 8 caracteres"],
                    ["letraMaiuscula", "Pelo menos 1 letra maiúscula"],
                    ["numero", "Pelo menos 1 número"],
                    [
                      "caractereEspecial",
                      "Pelo menos 1 caractere especial (!@#$%^&*)",
                    ],
                    ["senhasConferem", "As senhas coincidem"],
                  ].map(([chave, descricao]) => (
                    <ListItem key={chave}>
                      <ListItemIcon>
                        {requisitosSenha[chave] ? (
                          <CheckIcon color="success" />
                        ) : (
                          <CloseIcon color="error" />
                        )}
                      </ListItemIcon>

                      <Typography variant="body2">{descricao}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{
              borderRadius: "50px",
            }}
          >
            Cancelar
          </Button>

          <Button
            color="primary"
            onClick={confirmarEdicao}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{
              borderRadius: "50px",
            }}
            disabled={
              Boolean(novaSenha) &&
              !(
                requisitosSenha.minimoCaracteres &&
                requisitosSenha.letraMaiuscula &&
                requisitosSenha.numero &&
                requisitosSenha.caractereEspecial &&
                requisitosSenha.senhasConferem
              )
            }
          >
            Salvar Alterações
          </Button>
        </DialogActions>

      </Dialog>

      {/* ====================================================
          EDIÇÃO DE ANALISTA — VISUAL SRE
          Reutiliza os estados, handlers e validações existentes.
      ==================================================== */}

      {tituloSre && (
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              borderTop: "3px solid var(--sear-borda-forte, #087cf0)",
              borderRadius: "10px",
              overflow: "hidden",
            },
          }}
        >
          {/* CABEÇALHO */}

          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 2,
              bgcolor: "var(--sear-superficie, #ffffff)",
              borderBottom: "1px solid var(--sear-borda, #e5edf5)",
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 30,
                  height: 30,
                  color: "var(--sear-azul, #087cf0)",
                }}
              >
                <PersonOutlineRounded
                  sx={{ fontSize: 28 }}
                />

                <EditIcon
                  sx={{
                    position: "absolute",
                    right: -3,
                    bottom: -1,
                    fontSize: 15,
                    bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
                    borderRadius: 0.5,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="h2"
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {usuarioSelecionado?.regra === "analyst"
                  ? "Editar analista"
                  : "Editar usuário"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  color: "var(--sear-texto-secundario, #7b8798)",
                  fontSize: tamanhosControlesGlobais.descricao,
                }}
              >
                Atualize os dados do usuário cadastrado.
              </Typography>
            </Box>

            <IconButton
              aria-label="Fechar edição"
              onClick={handleCloseEditDialog}
              size="small"
              sx={{
                color: "var(--sear-texto-secundario, #71839a)",
                alignSelf: "flex-start",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* CONTEÚDO */}

          <DialogContent
            sx={{
              px: 2.5,
              pt: "18px !important",
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.6,
              }}
            >
              {/* NOME */}

              <Box>
                <Typography
                  component="label"
                  htmlFor="sre-edicao-nome"
                  sx={{
                    display: "block",
                    mb: 0.6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--sear-texto-secundario, #355476)",
                  }}
                >
                  Nome
                </Typography>

                {usuarioSelecionado?.regra === "analyst" ? (
                  <TextField
                    id="sre-edicao-nome"
                    fullWidth
                    size="small"
                    value={nomeEditado}
                    onChange={(e) =>
                      setNomeEditado(e.target.value)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        bgcolor: "var(--sear-superficie, #ffffff)",
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      border: "1px solid var(--sear-borda, #d7e7f7)",
                      borderRadius: 1,
                      color: "var(--sear-texto-secundario, #355476)",
                    }}
                  >
                    {usuarioSelecionado?.nome}
                  </Box>
                )}
              </Box>

              {/* E-MAIL */}

              <Box>
                <Typography
                  component="label"
                  htmlFor="sre-edicao-email"
                  sx={{
                    display: "block",
                    mb: 0.6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--sear-texto-secundario, #355476)",
                  }}
                >
                  E-mail institucional
                </Typography>

                <TextField
                  id="sre-edicao-email"
                  fullWidth
                  size="small"
                  type="email"
                  value={emailEditado}
                  onChange={(e) =>
                    setEmailEditado(e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: "var(--sear-superficie, #ffffff)",
                    },
                  }}
                />
              </Box>

              {/* ALTERAÇÃO DE SENHA */}

              <Box
                sx={{
                  borderTop: "1px solid var(--sear-borda, #e5edf5)",
                  pt: 1.6,
                  mt: 0.3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.7,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    Alterar senha
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "var(--sear-texto-secundario, #7b8798)",
                    }}
                  >
                    (opcional)
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    mt: 0.4,
                    mb: 1.4,
                    color: "var(--sear-texto-secundario, #7b8798)",
                    fontSize: 12,
                  }}
                >
                  Deixe os campos em branco para manter a senha atual.
                </Typography>

                {/* CAMPOS DE SENHA */}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="label"
                      htmlFor="sre-edicao-nova-senha"
                      sx={{
                        display: "block",
                        mb: 0.6,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--sear-texto-secundario, #355476)",
                      }}
                    >
                      Nova senha
                    </Typography>

<TextField
  id="sre-edicao-nova-senha"
  fullWidth
  size="small"
  type={mostrarSenha ? "text" : "password"}
  autoComplete="new-password"
  placeholder="Digite a nova senha..."
  value={novaSenha}
                      onChange={handleSenhaChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={
                                mostrarSenha
                                  ? "Ocultar nova senha"
                                  : "Mostrar nova senha"
                              }
                              onClick={() =>
                                setMostrarSenha(!mostrarSenha)
                              }
                            >
                              {mostrarSenha ? (
                                <VisibilityOffIcon
                                  sx={{ fontSize: 17 }}
                                />
                              ) : (
                                <VisibilityIcon
                                  sx={{ fontSize: 17 }}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="label"
                      htmlFor="sre-edicao-confirmar-senha"
                      sx={{
                        display: "block",
                        mb: 0.6,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--sear-texto-secundario, #355476)",
                      }}
                    >
                      Confirmar nova senha
                    </Typography>

<TextField
  id="sre-edicao-confirmar-senha"
  fullWidth
  size="small"
  type={
    mostrarConfirmarSenha
      ? "text"
      : "password"
  }
  autoComplete="new-password"
  placeholder="Confirme a nova senha..."
  value={confirmarSenha}
                      onChange={handleConfirmarSenhaChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={
                                mostrarConfirmarSenha
                                  ? "Ocultar confirmação da senha"
                                  : "Mostrar confirmação da senha"
                              }
                              onClick={() =>
                                setMostrarConfirmarSenha(
                                  !mostrarConfirmarSenha
                                )
                              }
                            >
                              {mostrarConfirmarSenha ? (
                                <VisibilityOffIcon
                                  sx={{ fontSize: 17 }}
                                />
                              ) : (
                                <VisibilityIcon
                                  sx={{ fontSize: 17 }}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* REQUISITOS DA SENHA */}

                <Box
                  sx={{
                    mt: 1.6,
                    p: 1.5,
                    border: "1px solid var(--sear-borda, #d2e5f8)",
                    borderRadius: 1.2,
                    bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
                  }}
                >
                  <Typography
                    sx={{
                      mb: 1,
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--sear-azul, #087cf0)",
                    }}
                  >
                    Requisitos da senha
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 0.7,
                    }}
                  >
                    {[
                      ["minimoCaracteres", "Mínimo de 8 caracteres"],
                      ["letraMaiuscula", "Uma letra maiúscula (A-Z)"],
                      ["numero", "Um número (0-9)"],
                      ["caractereEspecial", "Um caractere especial"],
                      ["senhasConferem", "As senhas coincidem"],
                    ].map(([chave, descricao]) => (
                      <Box
                        key={chave}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.7,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            border: "1px solid",
                            borderColor: requisitosSenha[chave]
                              ? "var(--sear-sucesso-borda, #1b9d7a)"
                              : "var(--sear-borda, #83a9cc)",
                            bgcolor: requisitosSenha[chave]
                              ? "#1b9d7a"
                              : "transparent",
                          }}
                        >
                          {requisitosSenha[chave] && (
                            <CheckIcon
                              sx={{
                                fontSize: 10,
                                color: "#ffffff",
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 11,
                            lineHeight: 1.35,
                            color: "var(--sear-texto-secundario, #526f8c)",
                          }}
                        >
                          {descricao}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* INDICADOR VISUAL DOS CRITÉRIOS */}

                  <Box sx={{ mt: 1.4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--sear-texto-secundario, #526f8c)",
                        }}
                      >
                        Critérios de segurança
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "var(--sear-texto-secundario, #7b8798)",
                        }}
                      >
                        {novaSenha
                          ? `${criteriosSenhaAtendidos} de 4 atendidos`
                          : "Não informada"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 0.6,
                      }}
                    >
                      {[0, 1, 2, 3].map((indice) => (
                        <Box
                          key={indice}
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            bgcolor:
                              indice < criteriosSenhaAtendidos
                                ? "var(--sear-acao-azul, #087cf0)"
                                : "var(--sear-superficie-hover, #ceddea)",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          {/* AÇÕES */}

          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              borderTop: "1px solid var(--sear-borda, #e5edf5)",
              bgcolor: "var(--sear-superficie-azul, #f8fafc)",
            }}
          >
            <Button
              onClick={handleCloseEditDialog}
              variant="outlined"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                borderColor: "var(--sear-borda, #c9def4)",
                color: "var(--sear-azul, #087cf0)",
                px: 2,
              }}
            >
              Cancelar
            </Button>

            <Button
              color="primary"
              onClick={confirmarEdicao}
              variant="contained"
              startIcon={<SaveRounded />}
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--sear-acao-azul, #087cf0)",
                px: 2,
                "&:hover": {
                  bgcolor: "var(--sear-acao-azul-hover, #0767d8)",
                },
              }}
              disabled={
                Boolean(novaSenha) &&
                !(
                  requisitosSenha.minimoCaracteres &&
                  requisitosSenha.letraMaiuscula &&
                  requisitosSenha.numero &&
                  requisitosSenha.caractereEspecial &&
                  requisitosSenha.senhasConferem
                )
              }
            >
              Salvar alterações
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );

  // ====================================================
  // TRANSIÇÃO E ESTRUTURA DA PÁGINA

  // ====================================================

  return tituloSre ? (
    <Box
      component={TransicaoPagina}
      data-pagina-rolavel=""
      variante="navegacao"
      sx={{
        width: "100%",
        py: 0,
      }}
    >
      {pagina}
    </Box>
  ) : (
    pagina
  );
};

export default UsuariosList;
